import { ErrorHandler, NgModule } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { AppComponent } from './app.component';
import { SidebarModule } from './sidebar/sidebar.module';
import { FooterModule } from './shared/footer/footer.module';
import { NavbarModule } from './shared/navbar/navbar.module';
import { AiAssistantButtonComponent } from './shared/AIAssistant/AIAssistantButton/ai-assistant-button.component';
import { AiAssistantComponent } from './shared/AIAssistant/ai-assistant/ai-assistant.component';
import { AiAgentComponent } from './shared/AIAgent/ai-agent/ai-agent.component';
import { AdminLayoutComponent } from './layouts/admin/admin-layout.component';
import { AuthLayoutComponent } from './layouts/auth/auth-layout.component';
import { ClientSettingsPanelComponent } from './shared/components/client-settings-panel/client-settings-panel.component';
import { AppRoutes } from './app.routing';
import { HTTP_INTERCEPTORS, HttpClientModule, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { JwtInterceptor } from './security/jwt.interceptor';
import { ErrorInterceptor } from './security/error.interceptor';
import { ApprovalSubmitTrackingInterceptor } from './security/approval-submit-tracking.interceptor';
import { SendBackReviewInterceptor } from './security/send-back-review.interceptor';
import { NgxLoadingModule } from 'ngx-loading';
import { ErrorHandlerService } from './services/error-handler.service';
import { ToastrModule } from 'ngx-toastr';
import { AgGridModule } from 'ag-grid-angular';
import { AnnouncementsModule } from './client/announcements/announcements.module';
import { EffectiveDatePopupComponent } from './components/client/workflow/forms/effective-date-popup/effective-date-popup.component';
import { EditDeleteBtnRenderV2Component } from './components/client/ag-grid/edit-delete-btn-render-v2.component';
import { BudgetToleranceApproversComponent } from './components/client/workflow/forms/budget-tolerance-approvers/budget-tolerance-approvers.component';
import { DuplicateInvoiceOverrideApproversComponent } from './components/client/workflow/forms/duplicate-invoice-override-approvers/duplicate-invoice-override-approvers.component';
import { MultiSelectModule } from 'primeng/multiselect';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';

import {
	IPublicClientApplication,
	PublicClientApplication,
	InteractionType,
	BrowserCacheLocation,
	LogLevel,
} from '@azure/msal-browser';
import {
	MsalGuard,
	MsalBroadcastService,
	MsalModule,
	MsalService,
	MSAL_GUARD_CONFIG,
	MSAL_INSTANCE,
	MsalGuardConfiguration,
	MsalRedirectComponent,
} from '@azure/msal-angular';
import { environment } from 'environments/environment';
import { AllCommunityModule, ModuleRegistry, provideGlobalGridOptions } from 'ag-grid-community';
import { AllEnterpriseModule, IntegratedChartsModule, LicenseManager } from 'ag-grid-enterprise';
import { AgChartsCommunityModule } from 'ag-charts-community';
import { appConfig } from './app.config';

LicenseManager.setLicenseKey(
	'Using_this_{AG_Grid}_Enterprise_key_{AG-088290}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Aclarian_LLC}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Aclarian_Portal}_only_for_{2}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_working_on_{Aclarian_Portal}_need_to_be_licensed___{Aclarian_Portal}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{15_June_2026}____[v3]_[01]_MTc4MTQ3ODAwMDAwMA==0176bb7418ff695cdb2d9d7641682b55'
);

// Register all community features
ModuleRegistry.registerModules([
	AllCommunityModule,
	AllEnterpriseModule,
	IntegratedChartsModule.with(AgChartsCommunityModule),
]);

// Mark all grids as using legacy themes
provideGlobalGridOptions({ theme: 'legacy' });
import { ShareGridLayoutModalComponent } from './components/client/share-grid-layout-modal/share-grid-layout-modal.component';
import { PaginatorModule } from 'primeng/paginator';
import { DropdownModule } from 'primeng/dropdown';
import { MatStepperModule } from '@angular/material/stepper';
import { MatIconModule } from '@angular/material/icon';
import { MainLayoutComponent } from './layouts/main-layout/main-layout.component';
import { RootLayoutComponent } from './layouts/root-layout/root-layout.component';
import { ModalModule } from 'ngx-bootstrap/modal';
import { NgxMaskModule } from 'ngx-mask';

@NgModule({
	imports: [
		BrowserAnimationsModule,
		FormsModule,
		ReactiveFormsModule,
		RouterModule.forRoot(AppRoutes, {
			useHash: true,
			enableTracing: false,
		}),
		NgbModule,
		HttpClientModule,
		SidebarModule,
		NavbarModule,
		FooterModule,
		NgxLoadingModule.forRoot({
			backdropBackgroundColour: 'rgba(0,0,0,0.3)',
		}),
		ToastrModule.forRoot(),
		ModalModule.forRoot(),
		AgGridModule,
		AnnouncementsModule,
		MultiSelectModule,
		MsalModule,
		MatStepperModule,
		MatIconModule,
		PaginatorModule,
		DropdownModule,
		AiAssistantButtonComponent,
		AiAssistantComponent,
		AiAgentComponent,
		MatFormFieldModule,
		MatAutocompleteModule,
		MatInputModule,
		MatTooltipModule,
		NgxMaskModule.forRoot(),
		ClientSettingsPanelComponent,
	],
	declarations: [
		AppComponent,
		MainLayoutComponent,
		AdminLayoutComponent,
		AuthLayoutComponent,
		EffectiveDatePopupComponent,
		EditDeleteBtnRenderV2Component,
		BudgetToleranceApproversComponent,
		DuplicateInvoiceOverrideApproversComponent,
		ShareGridLayoutModalComponent,
		RootLayoutComponent,
	],
	providers: [
		...appConfig.providers,
		{ provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: ApprovalSubmitTrackingInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: SendBackReviewInterceptor, multi: true },
		{ provide: ErrorHandler, useClass: ErrorHandlerService },
		{
			provide: MSAL_INSTANCE,
			useFactory: MSALInstanceFactory,
		},
		{
			provide: MSAL_GUARD_CONFIG,
			useFactory: MSALGuardConfigFactory,
		},
		MsalService,
		MsalGuard,
		MsalBroadcastService,
		provideHttpClient(withInterceptorsFromDi()),
	],
	exports: [],
	bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {
	constructor() {
		// eslint-disable-next-line no-console
		console.log('Working');
	}

	forDeepsource() {
		// eslint-disable-next-line no-console
		console.log('forDeepsource Working');
	}
}

export function MSALInstanceFactory(): IPublicClientApplication {
	const clientId = environment.msEntraClientId;
	const redirectUri = environment.baseUrl;

	const msalObj = new PublicClientApplication({
		auth: {
			clientId: clientId,
			authority: 'https://login.microsoftonline.com/common',
			redirectUri: redirectUri,
		},
		cache: {
			cacheLocation: BrowserCacheLocation.LocalStorage,
		},
		system: {
			loggerOptions: {
				loggerCallback,
				logLevel: LogLevel.Info,
				piiLoggingEnabled: false,
			},
		},
	});
	return msalObj;
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
	return {
		interactionType: InteractionType.Redirect,
		authRequest: {
			scopes: [environment.msEntraLoginScope],
		},
		loginFailedRoute: '/',
	};
}

export function loggerCallback(logLevel: LogLevel, message: string) {
	// eslint-disable-next-line no-console
	console.log(message);
}
